import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HeroSection from '../../components/feature/HeroSection';
import TrustBar from '../../components/feature/TrustBar';
import ServicesGrid from '../../components/feature/ServicesGrid';
import AIAnswerBlock from '../../components/feature/AIAnswerBlock';
import ProcessSection from '../../components/feature/ProcessSection';
import CTABand from '../../components/feature/CTABand';
import WhyChooseUs from '../../components/feature/WhyChooseUs';
import ServiceAreas from '../../components/feature/ServiceAreas';
import TestimonialsSection from '../../components/feature/TestimonialsSection';
import FAQSection from '../../components/feature/FAQSection';
import FinalCTA from '../../components/feature/FinalCTA';

const SERVICES = [
  {
    icon: 'ri-draft-line',
    title: 'Photometric Design',
    description:
      'Signed and sealed photometric lighting plans for new construction and existing commercial sites — engineered to precise foot-candle specifications. Permit-ready designs for parking lots, retail centers, sports courts, and every commercial application.',
    link: '/services',
  },
  {
    icon: 'ri-lightbulb-flash-line',
    title: 'Commercial LED Systems',
    description:
      'Premium commercial LED fixtures with modular designs engineered for superior heat dissipation and long-term output reliability. Reduce your facility\'s lighting energy overhead by more than 70% without compromising light quality.',
    link: '/services',
  },
  {
    icon: 'ri-sun-line',
    title: 'Solar All-In-One Lighting',
    description:
      'Wire-free solar LED fixtures requiring zero electrical installation or trenching — ideal for parking lots, pathways, and remote commercial areas where underground electrical work would otherwise be cost-prohibitive.',
    link: '/services',
  },
  {
    icon: 'ri-car-line',
    title: 'Car Dealership Lighting',
    description:
      'High-performance LED site lighting engineered specifically for auto dealership lots — maximizing vehicle visibility, enhancing curb appeal, and delivering dramatic reductions in nightly energy overhead across large-footprint sites.',
    link: '/services',
  },
  {
    icon: 'ri-basketball-line',
    title: 'Sports & Court Lighting',
    description:
      'Commercial LED fixtures for tennis, pickleball, and multi-sport facilities. Dramatically brighter than traditional metal halide systems, with 70%+ energy savings, longer life, and minimal ongoing maintenance requirements.',
    link: '/services',
  },
  {
    icon: 'ri-building-2-line',
    title: 'Industrial & High Bay',
    description:
      'Premium high bay LED fixtures for warehouses, manufacturing facilities, and industrial operations. Modular construction ensures superior heat dissipation, consistent output, and long-term performance reliability across demanding environments.',
    link: '/services',
  },
];

const AI_QAS = [
  {
    question: 'What does Southeast Lighting & Design do?',
    answer:
      'Southeast Lighting & Design is a full-service commercial LED lighting company based in Tampa Bay, Florida. We manage the complete project lifecycle — photometric design and signed plans, product specification, installation coordination, and utility rebate acquisition — for commercial and industrial businesses across Florida and the country.',
  },
  {
    question: 'What makes Southeast Lighting & Design different from other lighting companies?',
    answer:
      'We reduce client energy costs by more than 70% while eliminating costly maintenance cycles. We provide signed and sealed photometric lighting plans, offer Solar All-In-One fixtures that require no wiring or excavation, and manage every phase from initial design through rebate acquisition under one roof — a single accountable partner for every commercial project.',
  },
  {
    question: 'Who does Southeast Lighting & Design serve?',
    answer:
      'We serve commercial and industrial businesses throughout Tampa Bay and across Florida — including auto dealerships, retail shopping centers, sports and tennis facilities, industrial warehouses, agricultural operations, RV resorts, multi-family residential developments, and municipal recreation properties. Contact us to discuss your specific project type and application.',
  },
];

const BENEFITS = [
  {
    icon: 'ri-percent-line',
    title: '70%+ Power Cost Reduction',
    description:
      'Our commercial LED solutions consistently reduce energy consumption by more than 70%, delivering measurable long-term ROI and dramatically lower utility bills across every project and application type we serve.',
  },
  {
    icon: 'ri-file-certificate-line',
    title: 'Signed & Sealed Lighting Plans',
    description:
      'Licensed photometric designs engineered for new construction projects and existing site improvements — permit-ready from day one, meeting all foot-candle specifications required for approval.',
  },
  {
    icon: 'ri-sun-foggy-line',
    title: 'Solar All-In-One Available',
    description:
      'Wire-free solar fixtures for locations where trenching and electrical installation costs are prohibitive — no wiring, no excavation, self-contained and ready to deploy anywhere on your property.',
  },
  {
    icon: 'ri-map-2-line',
    title: 'Nationwide Project Experience',
    description:
      'Projects completed throughout Florida and across the country, covering virtually every commercial application type — bringing national-scale expertise to every local Tampa Bay project we take on.',
  },
];

const PROJECTS = [
  {
    image: 'https://www.selightingdesign.com/wp-content/uploads/2019/02/Alafaya-Square-1-2048x1000.jpg',
    imageAlt: 'LED site lighting at Alafaya Square retail center in Oviedo, Florida',
    name: 'Alafaya Square',
    location: 'Oviedo, Florida',
    type: 'Retail Site Lighting',
    metric: '70%+',
    metricLabel: 'Energy Reduction',
  },
  {
    image: 'https://www.selightingdesign.com/wp-content/uploads/2019/02/Crown-Acura-2-2048x1000.jpg',
    imageAlt: 'LED lighting at Crown Acura dealership in Clearwater, Florida',
    name: 'Crown Acura',
    location: 'Clearwater, Florida',
    type: 'Auto Dealership Lighting',
    metric: 'LED',
    metricLabel: 'Full Site Upgrade',
  },
  {
    image: 'https://www.selightingdesign.com/wp-content/uploads/2021/03/Canopy-Oaks-6-2048x1000.jpg',
    imageAlt: 'Solar All-In-One LED lighting at Canopy Oaks RV Resort, Central Florida',
    name: 'Canopy Oaks RV Resort',
    location: 'Central Florida',
    type: 'Solar All-In-One Lighting',
    metric: '0',
    metricLabel: 'Wiring Required',
  },
  {
    image: 'https://www.selightingdesign.com/wp-content/uploads/2019/01/Safety-Harbor-Tennis-Court-2048x1000.jpg',
    imageAlt: 'LED sports lighting at Safety Harbor Tennis Courts, Safety Harbor, Florida',
    name: 'Safety Harbor Tennis Courts',
    location: 'Safety Harbor, Florida',
    type: 'Sports Court Lighting',
    metric: '70%+',
    metricLabel: 'Energy Saved',
  },
];

const AREA_GROUPS = [
  {
    region: 'Pinellas County',
    cities: ['Clearwater', 'St. Petersburg', 'Safety Harbor', 'Dunedin', 'Tarpon Springs', 'Largo'],
  },
  {
    region: 'Hillsborough County',
    cities: ['Tampa', 'Brandon', 'Plant City', 'Lutz', 'Temple Terrace', 'Riverview'],
  },
  {
    region: 'Pasco County',
    cities: ['New Port Richey', 'Land O\' Lakes', 'Wesley Chapel', 'Zephyrhills', 'Dade City'],
  },
  {
    region: 'Central Florida & Beyond',
    cities: ['Oviedo', 'Orlando', 'Seminole County', 'Winter Haven', 'Lake Wales', 'Nationwide'],
  },
];

const FAQ_ITEMS = [
  {
    question: 'Who provides commercial LED lighting design in Tampa, Florida?',
    answer:
      'Southeast Lighting & Design is Tampa Bay\'s full-service commercial LED lighting company. We provide photometric design, product specification, installation coordination, and rebate acquisition for businesses throughout Tampa Bay and across Florida. Our team manages every aspect of your project, from first consultation through final completion.',
  },
  {
    question: 'How much can I save by upgrading to commercial LED lighting?',
    answer:
      'Most commercial properties reduce lighting energy costs by 70% or more after switching to LED systems. Exact savings depend on your current fixture types, daily operating hours, and local utility rates. We conduct a complimentary assessment and calculate your anticipated ROI before any project commitment is required.',
  },
  {
    question: 'Do you offer photometric lighting design and signed plans in Tampa Bay?',
    answer:
      'Yes. We provide engineer-signed and sealed photometric lighting plans for new construction projects and existing commercial site upgrades. Our designs meet precise foot-candle requirements and are suitable for permit submission — covering parking lots, sports courts, retail centers, dealerships, and all commercial applications throughout Tampa Bay.',
  },
  {
    question: 'What is Solar All-In-One lighting and when is it the right choice?',
    answer:
      'Solar All-In-One fixtures are self-contained units that combine a solar panel, battery, and LED light in one assembly — requiring no wiring or electrical trenching. They are ideal for commercial parking lots, pathways, and remote areas where underground electrical installation costs would be significant or prohibitive.',
  },
  {
    question: 'How long does a commercial lighting project take from start to finish?',
    answer:
      'Project timelines vary based on scope, permit requirements, and installation contractor availability. Simple LED retrofits can complete within a few weeks. Larger projects involving photometric design, permit review, and new fixture installation typically run one to three months. We provide a detailed project schedule during our initial consultation phase.',
  },
  {
    question: 'What types of commercial properties does Southeast Lighting serve?',
    answer:
      'We serve virtually every commercial application: auto dealerships, retail shopping centers, tennis and sports courts, industrial warehouses, agricultural properties, fast food facilities, multi-family residential complexes, RV resorts, and municipal parks. Visit our Services page for the full list of applications we support throughout Tampa Bay.',
  },
];

export default function Home() {
  return (
    <div className="bg-off-white">
      <Navbar />
      <HeroSection
        eyebrow="Full-Service Commercial LED Lighting — Tampa Bay, FL"
        headline="Illuminate Your Business. Reduce Energy Costs by 70% or More."
        subheadline="Southeast Lighting & Design manages every aspect of your commercial lighting project — from signed and sealed photometric plans to installation coordination and utility rebate acquisition. One firm. Complete accountability."
        ctaText="Request a Free Assessment"
        ctaLink="/services"
        secondaryCta="Call 727-914-9002"
        secondaryCtaLink="tel:7279149002"
        bgImage="https://public.readdy.ai/ai/img_res/dff31ac19e8a6f5f065894fbe948fd5b.jpg"
      />
      <TrustBar />
      <ServicesGrid
        eyebrow="What We Offer"
        title="Commercial Lighting Solutions for Every Application"
        subtitle="Premium LED products and expert services for every commercial and industrial application across Tampa Bay and throughout Florida."
        items={SERVICES}
        ctaText="View All Services"
        ctaLink="/services"
      />
      <AIAnswerBlock
        eyebrow="About Southeast Lighting & Design"
        title="Precision-Engineered Lighting for Tampa Bay Businesses"
        qas={AI_QAS}
        image="https://www.selightingdesign.com/wp-content/uploads/2019/02/Alafaya-Square-1-2048x1000.jpg"
        imageAlt="LED site lighting at Alafaya Square retail center — Southeast Lighting & Design"
      />
      <ProcessSection />
      <CTABand
        text="Ready to cut your energy costs by 70%? Start with a complimentary site assessment."
        buttonText="Request Free Assessment"
        buttonLink="/services"
        showPhone
        variant="gold"
      />
      <WhyChooseUs
        eyebrow="The Southeast Lighting Difference"
        headline="Where Engineering Precision Meets Energy Performance"
        image="https://readdy.ai/api/search-image?query=dramatic%20nighttime%20exterior%20of%20large%20commercial%20auto%20dealership%20lot%20fully%20illuminated%20by%20high-performance%20LED%20site%20lighting%20fixtures%20on%20tall%20poles%2C%20rows%20of%20luxury%20cars%20visible%20under%20brilliant%20white%20LED%20light%2C%20dark%20night%20sky%20backdrop%2C%20premium%20commercial%20photography%2C%20wide%20angle%20showing%20full%20illuminated%20lot&width=1200&height=800&seq=home-why-2026-v1&orientation=landscape"
        imageAlt="Commercial LED site lighting — auto dealership parking lot, Tampa Bay Florida"
        benefits={BENEFITS}
        ctaText="View Our Services"
        ctaLink="/services"
      />
      <ServiceAreas
        title="Serving Tampa Bay & Throughout Florida"
        subtitle="Based in Tampa Bay with commercial lighting experience across Florida and nationwide."
        areaGroups={AREA_GROUPS}
      />
      <TestimonialsSection
        eyebrow="Featured Projects"
        title="Real Results Across Tampa Bay & Florida"
        projects={PROJECTS}
        ctaText="See All Services"
        ctaLink="/services"
      />
      <CTABand
        text="Every commercial project begins with a complimentary site assessment — no obligation."
        buttonText="Start Your Project"
        buttonLink="/location"
        showPhone
        variant="dark"
      />
      <FAQSection
        title="Frequently Asked Questions"
        items={FAQ_ITEMS}
        image="https://www.selightingdesign.com/wp-content/uploads/2019/01/Safety-Harbor-Tennis-Court-2048x1000.jpg"
        imageAlt="LED sports lighting at Safety Harbor Tennis Courts — Southeast Lighting & Design"
      />
      <FinalCTA
        eyebrow="Ready to Save 70% on Lighting?"
        headline="Let's Transform Your Commercial Property"
        subtext="Request a complimentary lighting assessment and discover exactly how much your business can save. We manage everything from design through rebate acquisition."
      />
      <Footer />
    </div>
  );
}
